import { ReactNode } from "react";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink } from "react-router-dom";
import { GovItemDetail } from "../../../../utils/icGovernance";
import {
  StatusChip,
  clientLabel,
  dateOnly,
  dateTime,
  itemUrlById,
  kindLabel,
  partyLabel,
  relationLabel,
  shortDate,
  vendorLabel,
} from "../governanceStatus";
import SideCard from "./SideCard";
import { GovSettingsOrNull } from "./types";
import { FAINT, MONO, TEXT, isSafeUrl } from "./ui";

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Label left, value right — compact rows for the side column. */
const KeyValueRows = ({ rows }: { rows: { label: string; value: ReactNode }[] }) => (
  <Box component="dl" sx={{ m: 0 }}>
    {rows.map((r) => (
      <Box
        key={r.label}
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2, py: 0.5 }}
      >
        <Typography component="dt" variant="body2" sx={{ color: FAINT, flexShrink: 0 }}>
          {r.label}
        </Typography>
        <Typography
          component="dd"
          variant="body2"
          sx={{ m: 0, color: TEXT, fontWeight: 600, textAlign: "right", overflowWrap: "anywhere", minWidth: 0 }}
        >
          {r.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

/** Lead party, owners (tester / support for UAT tests), dates. */
export const DetailsCard = ({ item, settings }: { item: GovItemDetail; settings: GovSettingsOrNull }) => {
  const uat = item.kind === "UAT_TEST";
  const rows: { label: string; value: ReactNode }[] = [
    { label: "Lead party", value: partyLabel(item.leadParty, settings) },
    { label: `${clientLabel(settings)} ${uat ? "tester" : "owner"}`, value: item.clientOwner || "—" },
    { label: `${vendorLabel(settings)} ${uat ? "support" : "owner"}`, value: item.vendorOwner || "—" },
  ];
  if (item.assignedTo) rows.push({ label: "Assigned to", value: item.assignedTo });
  rows.push(
    { label: "Due date", value: dateOnly(item.dueDate) },
    { label: "Created", value: dateTime(item.createdAt) },
    { label: "Updated", value: dateTime(item.updatedAt) },
  );
  return (
    <SideCard title="Details">
      <KeyValueRows rows={rows} />
    </SideCard>
  );
};

/** Evidence links (opened in a new tab; only http(s) URLs become links). */
export const EvidenceCard = ({ item }: { item: GovItemDetail }) => (
  <SideCard title="Evidence">
    {(item.evidence ?? []).length === 0 ? (
      <Typography variant="body2" sx={{ color: FAINT }}>
        No evidence yet.
      </Typography>
    ) : (
      <Stack spacing={1.25}>
        {(item.evidence ?? []).map((e, i) => {
          const meta = [e.source, e.at ? shortDate(e.at) : null].filter(Boolean).join(" · ");
          return (
            <Box key={`${i}-${e.url}`} sx={{ minWidth: 0 }}>
              {isSafeUrl(e.url) ? (
                <MuiLink
                  href={e.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: 600, overflowWrap: "anywhere", display: "inline-flex", alignItems: "center", gap: 0.5 }}
                >
                  {e.label || e.url}
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </MuiLink>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                  {e.label || e.url}
                </Typography>
              )}
              {meta ? (
                <Typography variant="caption" component="p" sx={{ color: FAINT }}>
                  {meta}
                </Typography>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    )}
  </SideCard>
);

/** Linked items: relation, display id (links to it), status chip and title. */
export const LinksCard = ({ item }: { item: GovItemDetail }) => (
  <SideCard title="Links">
    {(item.links ?? []).length === 0 ? (
      <Typography variant="body2" sx={{ color: FAINT }}>
        No linked items.
      </Typography>
    ) : (
      <Stack spacing={1.5}>
        {(item.links ?? []).map((l, i) => {
          const label = l.target?.displayId || l.targetDisplayId;
          const kind = l.target?.kind ?? l.targetKind;
          return (
            <Box key={`${l.relation}-${l.targetId}-${i}`} sx={{ minWidth: 0 }}>
              <Typography variant="caption" component="p" sx={{ color: FAINT }}>
                {capitalize(relationLabel(l.relation))}
                {kind ? ` · ${kindLabel(kind)}` : ""}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: "wrap" }}>
                <MuiLink
                  component={RouterLink}
                  to={itemUrlById(label || l.targetId)}
                  sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 14 }}
                >
                  {label || "Linked item"}
                </MuiLink>
                {l.target ? <StatusChip kind={l.target.kind} status={l.target.status} /> : null}
              </Stack>
              {l.target?.title ? (
                <Typography variant="body2" sx={{ color: TEXT, overflowWrap: "anywhere", mt: 0.25 }}>
                  {l.target.title}
                </Typography>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    )}
  </SideCard>
);
